import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import { colors } from '../theme';

const ChemicalStorageTanks: React.FC = () => {
  const tankTypesAndSizes = [
    '22 – 20000 gallons in vertical storage tanks',
    '35 – 12500 gallons in double wall storage tanks',
    '35 – 440 gallons in chemical feed stations',
    '65 – 335 gallons in chemical injection tanks',
    '120 – 100 gallons in used collection tanks',
    '90 – 875 gallons in containment basins',
    '175 – 6900 gallons in secondary containment',
    '30 – 500 gallons in open top tanks',
    '55 – 500 in open top total drain tanks',
    '30 – 3400 in horizontal tanks',
    '30 – 325 in rectangular PCO tanks',
    '60 – 850 gallons in closed top total drain tanks',
    '15 – 13000 gallons in cone bottom tanks',
    '120 – 330 gallons in standard square stackable tanks',
    '120 – 330 gallons in premium square stackable tanks',
    '220 – 330 gallons in standard Ultratainer tanks',
    '220 – 330 gallons in wide mouth Ultratainer tanks',
    '550 gallons in Megatainer tank',
    '65 – 20000 gallons in commercial tanks',
  ];

  const customFeatures = [
    'Tank Color',
    'Protective Wrap for Shipping',
    'Tank Saver Platforms',
    'Leak Detection',
    'Ultrasonic Level',
    'Seismic Restraints',
    'Sight Glass',
    'Reverse Float Indicator',
    'Reverse Stenciled Calibration Board',
    'External Fill',
    'Level Gauge',
    'Draw Tube',
    'Poly & Metal Tank Stands',
    'Band Assemblies',
    'Hinged Lids for Open Top Tanks',
    'Bolted & Sealed Lids for Open Top Tanks',
    'Mixer Mounts for Open Top Tanks',
    'Total Drain Fittings and Vents for Open Top Tanks',
    'Hoop Assemblies',
    'Spil-Tainer Drum Containment Units',
    'Flexmaster Flexible Connections',
    'Metal & FRP Ladders/ Cages',
    'Insulation',
    'Heat Trace',
    'Name Plates',
    'Stenciling',
    'Wet Stamps',
    'Gallonage Indicators',
    'Sight Gauge',
    'External Downpipe',
    'PVC/ CPVC Piping',
    'Hydrostatic Testing',
    'Gel Test',
    'ASTM Documentation',
    'Custom Hole Cuts',
    'Siphon Tubes',
    'U-Vents',
    'Manways',
    'Fill Caps',
    'Mushroom Vents',
    'Flange Adapters',
    'Gaskets',
    'Bulkhead Fittings',
    'Unitized Molded Outlet – SUMO Fitting',
    'Self-Aligning Bulkhead Fittings',
    'PP Bolted Total Drain Fittings – SS Bolts',
    'Stainless Steel Fittings',
    'Bolted Double Flanges Fittings',
    'Bolted Double Flange Fittings Encapsulated Bolts in SS/ Hastelloy/ Titanium',
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label="Products"
              component={Link}
              to="/products"
              clickable
              sx={{
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Chemical Storage Tanks
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              The largest selection of high-density polyethylene tanks in the industry
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)` }} />

      {/* High-Density Linear & Crosslink Polyethylene Tanks Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Our Specialty
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 4,
                  mt: 1,
                }}
              >
                High-Density Linear & Crosslink Polyethylene Tanks
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                We offer the largest product selection of High-Density Linear and Crosslink Polyethylene tanks in the industry. We provide customized chemical storage tanks in a variety of materials and sizes to hold various media types for any application.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                Our knowledgeable team will work with you to select a chemical storage tank that meets your exact specifications. We adhere to ASTM D 1998 standards for material processing to ensure your chemical storage tank meets your quality specifications.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                At Process Control, we specialize in single and double-wall containment units, horizontal tanks, commercial tanks, chemical feed stations, chemical day tanks, and IBC totes. Our engineering team will work with you to design your custom tank.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                Request Custom Tank
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/PLC-image3.jpg"
                  alt="Chemical Storage Tank"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Chemical Storage Tank Materials Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5} order={{ xs: 2, md: 1 }}>
              <Box
                component="img"
                src="/PLC-image4.jpg"
                alt="Chemical Storage Tank Materials"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                }}
              />
            </Grid>
            <Grid item xs={12} md={7} order={{ xs: 1, md: 2 }}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Materials
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 4,
                  mt: 1,
                }}
              >
                Chemical Storage Tank Materials
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                We partner with high-quality, chemical storage tank manufacturers to offer our customers durable chemical storage solutions.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                We provide customers Snyder chemical storage tanks manufactured from HDLPE (High Density Linear Polyethylene) or XLPE (Cross-Linked Polyethylene). If your final application requires fiberglass-reinforced plastic, we also offer custom Plas-Tanks chemical storage tanks and vessels.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                The chemicals you need to store directly impact the material used on your chemical storage tank.
              </Typography>
              <Button
                component="a"
                href="/chemical-resistance-chart.pdf"
                target="_blank"
                variant="outlined"
                color="secondary"
                startIcon={<DescriptionIcon />}
              >
                Chemical Resistance Chart
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tank Types & Sizes Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Options
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.secondary,
                mt: 1,
                mb: 2,
              }}
            >
              Chemical Storage Tank Types & Sizes
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.textSecondary,
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              To better serve our customers, we offer various types and sizes of chemical storage tanks. No matter how small or large of a tank you need, we have the solution for you.
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: colors.border,
              mb: 6,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.secondary,
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                We provide the following chemical storage tank types and sizes:
              </Typography>
              <Grid container spacing={1}>
                {tankTypesAndSizes.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: colors.primary,
                          mt: '0.5em',
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.textSecondary,
                          lineHeight: 1.5,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Images */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/PLC-Panel-image1.jpg"
                alt="Chemical Storage Tank"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/PLC-Panel-image-2.jpg"
                alt="Chemical Storage Tank"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Custom Features Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.secondary,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Customization
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#FFFFFF',
                mt: 1,
                mb: 2,
              }}
            >
              Custom Chemical Storage Tank Features
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              At Process Control, we know that each project and application is unique, which will require customized chemical storage tank design options.
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              mb: 4,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.secondary,
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                We partner with Snyder to offer our customers custom features including:
              </Typography>
              <Grid container spacing={1}>
                {customFeatures.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: colors.primary,
                          mt: '0.5em',
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.textSecondary,
                          lineHeight: 1.5,
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                mb: 4,
              }}
            >
              The team at Process Control are industry experts and will create a customized chemical storage tank solution that meets your requirements.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primaryDark,
                },
              }}
            >
              Get More Info
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Back to Products */}
      <Box
        sx={{
          py: 4,
          background: '#FFFFFF',
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <Button
          component={Link}
          to="/products"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
        >
          Back to All Products
        </Button>
      </Box>
    </Box>
  );
};

export default ChemicalStorageTanks;
